import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Shield, Download, Upload, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  const handleProfileUpdate = (e) => {
    e.preventDefault();
    toast.info('Profile update feature coming soon');
  };

  const handleExportBackup = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/backup/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `vape-avenue-backup-${ts}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export backup');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const confirmMsg = importMode === 'replace'
      ? 'REPLACE mode will DELETE all existing data before importing. Continue?'
      : 'MERGE mode will upsert records from the backup (existing records with the same id will be overwritten). Continue?';
    if (!window.confirm(confirmMsg)) {
      event.target.value = '';
      return;
    }

    setIsImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/backup/import?mode=${importMode}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const summary = res.data.summary || {};
      const counts = Object.entries(summary)
        .map(([k, v]) => `${k}: ${v.total}`)
        .join(', ');
      toast.success(`Restore complete — ${counts}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import backup');
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight" data-testid="settings-title">Settings</h1>
        <p className="text-muted-foreground font-body mt-2">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information */}
        <div className="bg-card border border-border rounded-lg p-6" data-testid="profile-section">
          <div className="flex items-center gap-3 mb-6">
            <User className="text-primary" size={24} strokeWidth={1.5} />
            <h2 className="font-heading font-bold text-2xl">Profile Information</h2>
          </div>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
              <Input
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className="mt-1.5"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Email</Label>
              <Input
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                type="email"
                className="mt-1.5"
                data-testid="profile-email-input"
              />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="update-profile-btn">
              Update Profile
            </Button>
          </form>
        </div>

        {/* Account Details */}
        <div className="bg-card border border-border rounded-lg p-6" data-testid="account-section">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="text-primary" size={24} strokeWidth={1.5} />
            <h2 className="font-heading font-bold text-2xl">Account Details</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Role</Label>
              <div className="mt-1.5 px-4 py-2 bg-secondary rounded-md border border-border">
                <span className="font-mono text-sm uppercase">{user?.role}</span>
              </div>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">User ID</Label>
              <div className="mt-1.5 px-4 py-2 bg-secondary rounded-md border border-border font-mono text-sm break-all">
                {user?.id}
              </div>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Account Created</Label>
              <div className="mt-1.5 px-4 py-2 bg-secondary rounded-md border border-border font-mono text-sm">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backup & Restore (Admin only) */}
      {isAdmin && (
        <div className="mt-6 bg-card border border-border rounded-lg p-6" data-testid="backup-section">
          <div className="flex items-center gap-3 mb-2">
            <Download className="text-primary" size={24} strokeWidth={1.5} />
            <h2 className="font-heading font-bold text-2xl">Backup &amp; Restore</h2>
          </div>
          <p className="text-muted-foreground text-sm mb-6">
            Download a full JSON snapshot of all stores, categories, products, variants, users, and logs.
            Upload it to any environment (e.g. your deployed app) to restore the data.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Export */}
            <div className="border border-border rounded-md p-4">
              <Label className="font-mono text-xs uppercase tracking-widest">Download Backup</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Exports every collection to a single JSON file.
              </p>
              <Button
                onClick={handleExportBackup}
                disabled={isExporting}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="export-backup-btn"
              >
                <Download size={16} className="mr-2" />
                {isExporting ? 'Exporting…' : 'Download Backup'}
              </Button>
            </div>

            {/* Import */}
            <div className="border border-border rounded-md p-4">
              <Label className="font-mono text-xs uppercase tracking-widest">Restore From Backup</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Upload a backup JSON file produced by this app.
              </p>

              <div className="space-y-2 mb-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="import-mode"
                    value="merge"
                    checked={importMode === 'merge'}
                    onChange={() => setImportMode('merge')}
                    data-testid="import-mode-merge"
                  />
                  <span><span className="font-mono uppercase">Merge</span> — upsert by id (recommended)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="import-mode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                    data-testid="import-mode-replace"
                  />
                  <span className="flex items-center gap-1">
                    <span className="font-mono uppercase">Replace</span>
                    <AlertTriangle size={14} className="text-yellow-500" />
                    — wipes existing data first
                  </span>
                </label>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={handleImportBackup}
                className="hidden"
                data-testid="import-backup-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                variant="outline"
                className="w-full"
                data-testid="import-backup-btn"
              >
                <Upload size={16} className="mr-2" />
                {isImporting ? 'Restoring…' : 'Upload & Restore'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
